import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { useQuery } from '@tanstack/react-query';
import GiftCard from './GiftCard';
import GiftCardActivationModal from './GiftCardActivationModal';
import { fetchUserGiftCards } from '@/services/giftCard/giftCardService';
import { getUID } from '@/utils';
import styles from './GiftCards.module.css';

/**
 * GiftCards Component
 * Main component displaying wallet balance and gift cards list
 * Fetches user's active gift cards from API
 */

// Transform API response to component format
const transformGiftCardData = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) return [];
  
  return apiData.map((item) => {
    const giftCard = item.giftcards || {};
    const userGiftCard = item.usergiftcards || null; // Can be null
    const deposit = item.deposit || {};
    const wager = item.wager || {};
    
    // Determine status - handle null usergiftcards
    let status = 'unclaimed';
    let isActivated = false;
    let activatedDate = null;
    
    if (userGiftCard) {
      if (userGiftCard.status === 'Activated') {
        status = 'activated';
        isActivated = true;
      } else if (userGiftCard.status === 'Claimed') {
        status = 'claimed';
        isActivated = true;
      }
      activatedDate = userGiftCard.startDate || null;
    }
    
    // Calculate wager values
    const amount = giftCard.amount || 0;
    const wagerTimes = giftCard.wagerStatus ? (giftCard.wagerTimes || 0) : 0;
    
    // Use API's requiredWagerUSD if available, otherwise calculate
    const calculatedTotalWager = amount * wagerTimes;
    const totalWager = (wager && wager.requiredWagerUSD) ? wager.requiredWagerUSD : calculatedTotalWager;
    const currentWager = (wager && wager.totalUsdWager) ? wager.totalUsdWager : 0;
    
    return {
      cardId: giftCard.id?.toString() || '',
      title: giftCard.description || `Gift Card ${giftCard.uniqueKey || ''}`,
      amount: amount,
      staticValue: amount, // Static bonus value
      wagerRequirement: wagerTimes, // Wager multiplier
      status: status,
      description: giftCard.description || '',
      expiryDate: giftCard.endDate || null,
      activationCode: giftCard.uniqueKey || '',
      requiresDeposit: giftCard.depositStatus || false,
      depositAmount: giftCard.depositAmount || 0,
      currentWager: currentWager,
      totalWager: totalWager, // Use API value or calculated
      isActivated: isActivated,
      activatedDate: activatedDate,
      // API specific fields
      periodDays: giftCard.periodDays,
      allUserStatus: giftCard.allUserStatus,
      isActive: giftCard.isActive,
      createdAt: giftCard.createdAt,
      updatedAt: giftCard.updatedAt,
      // Status details - handle missing deposit/wager objects
      depositStatus: deposit ? deposit.status : undefined,
      depositMessage: deposit ? deposit.message : undefined,
      wagerStatus: wager ? wager.status : undefined,
      requiredWagerUSD: (wager && wager.requiredWagerUSD) ? wager.requiredWagerUSD : totalWager,
    };
  });
};


const GiftCards = forwardRef((props, ref) => {
  const [giftCards, setGiftCards] = useState([]);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const walletRef = useRef(null);
  const prevBalanceRef = useRef(0);
  const userId = getUID();

  // Fetch user's active gift cards from API
  const { data: apiData, isLoading, error, refetch } = useQuery({
    queryKey: ['userGiftCards', userId],
    queryFn: () => fetchUserGiftCards(userId),
    enabled: !!userId, // Only fetch if userId exists
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every 60 seconds
    retry: 2,
    retryDelay: 1000,
  });

  // Transform API data and update state
  useEffect(() => {
    if (apiData?.success && apiData?.data) {
      const transformedCards = transformGiftCardData(apiData.data);
      setGiftCards(transformedCards);
    } else if (!isLoading && !userId) {
      // No user logged in - show empty state or keep empty
      setGiftCards([]);
    }
  }, [apiData, isLoading, userId]);

  // Expose method to parent component
  useImperativeHandle(ref, () => ({
    openActivationModal: () => {
      setShowActivationModal(true);
    },
    refetchGiftCards: () => {
      refetch();
    }
  }));

  // Calculate wallet balance from claimed cards
  const walletBalance = giftCards
    .filter(card => card.status === 'claimed')
    .reduce((total, card) => total + card.amount, 0);

  // Animate wallet balance update
  useEffect(() => {
    if (walletRef.current && walletBalance !== prevBalanceRef.current) {
      // Pulse animation when balance increases
      gsap.fromTo(
        walletRef.current,
        { scale: 1 },
        {
          scale: 1.1,
          duration: 0.3,
          yoyo: true,
          repeat: 1,
          ease: 'power2.out',
        }
      );
      prevBalanceRef.current = walletBalance;
    }
  }, [walletBalance]);

  // Handle gift card activation by code
  const handleActivate = async (code) => {
    // After activation, refetch gift cards to get updated data
    setTimeout(() => {
      refetch();
    }, 1000);
  };

  // Handle card claim (only after wager requirement is met)
  const handleClaim = async (cardId) => {
    // After claiming, refetch gift cards to get updated data
    setTimeout(() => {
      refetch();
    }, 1000);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.giftCardsContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.loader}>
            <span className={styles.loaderText}>loading</span>
            <span className={styles.load}></span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.giftCardsContainer}>
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>Error loading gift cards. Please try again.</p>
        </div>
      </div>
    );
  }

  // No user logged in
  if (!userId) {
    return (
      <div className={styles.giftCardsContainer}>
        <div className={styles.emptyState}>
          <p className={styles.emptyStateText}>Please log in to view your gift cards</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.giftCardsContainer}>
      {/* Gift Cards Grid */}
      <motion.div
        className={styles.cardsGrid}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        {giftCards.length > 0 ? (
          giftCards.map((card, index) => (
            <GiftCard
              key={card.cardId}
              card={card}
              onClaim={handleClaim}
              onActivate={() => setShowActivationModal(true)}
              index={index}
            />
          ))
        ) : (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateText}>No active gift cards found</p>
          </div>
        )}
      </motion.div>

      {/* Activation Modal */}
      <GiftCardActivationModal
        isOpen={showActivationModal}
        onClose={() => setShowActivationModal(false)}
        onActivate={handleActivate}
      />
    </div>
  );
});

GiftCards.displayName = 'GiftCards';

export default GiftCards;
